/**
 * Список сделок ОДНОГО кошелька в ОДНОМ окне: как он набирал позицию.
 *
 * Чистый модуль по правилу lib/*.ts: vitest в этом проекте включает только
 * `*.test.ts`, компонентных тестов нет, поэтому всё, что решает, ЧТО за число
 * видит оператор, живёт здесь, а в .tsx остаётся разметка.
 *
 * # На какой вопрос отвечает список
 *
 * Панель накопления говорит «сколько он набрал и какой ролью» — одним числом на
 * кошелёк. Здесь — как он к этому числу пришёл: зашёл на седьмой секунде или
 * дождался последней минуты, долил разом или размазал по сотне филлов, брал
 * агрессией или стоял в стакане, и не начал ли в середине окна раздавать
 * набранное. Ради этого и заведена секунда от старта окна.
 */

import type { MarketWalletTrade, MarketWalletTrades, Token } from "../api/types";
import { plural } from "./format";
import { roundToFixed, parse } from "./money";
import { formatShares } from "./tape";

/** Одна строка списка, уже пригодная к печати. */
export interface WalletTradeLine {
  /** Ключ React. Хеша транзакции мало: у одной их бывает несколько строк. */
  key: string;
  /** Время сделки по местным часам, до секунды: «16:50:07». */
  time: string;
  /**
   * Секунда от начала окна: «+7 с», «+2:19». Пусто, когда начала окна не знаем.
   *
   * Со знаком «+» всегда, даже в первую секунду: это смещение от старта, а не
   * момент времени, и знак отличает одно от другого с одного взгляда.
   *
   * Отрицательное законно и содержательно: кошелёк мог купить токен ДО открытия
   * окна — бинарное окно гасится целиком, и такие строки в свод входят.
   */
  tau: string;
  side: "BUY" | "SELL";
  /** Имя исхода из справочника рынка. Пусто — токена в справочнике нет. */
  outcome: string;
  /** «тейкер», «мейкер» либо пусто — роль в этой транзакции не разложена. */
  role: string;
  size: string;
  price: string;
  usd: string;
}

/**
 * Строки списка. Пустой массив — показывать нечего.
 *
 * Порядок НЕ трогается: он задан сервером по возрастанию времени, и вопрос «как
 * набирал» читается сверху вниз. Переставлять его здесь значило бы завести
 * второе место, решающее порядок.
 */
export function walletTradeLines(
  data: MarketWalletTrades | undefined,
  tokens: readonly Token[],
  windowStart: Date | null,
): WalletTradeLine[] {
  if (!data?.trades?.length) return [];
  const names = new Map(tokens.map((t) => [t.asset_id, t.outcome]));
  return data.trades.map((t, i) => ({
    key: `${t.tx_hash ?? ""}:${i}`,
    time: formatClock(t.at),
    tau: tauLabel(t.at, windowStart),
    side: t.side === "SELL" ? "SELL" : "BUY",
    outcome: names.get(t.asset_id) ?? "",
    role: roleLabel(t),
    size: formatShares(t.size),
    // Цена — до трёх знаков: шаг цены на этих рынках доходит до 0.001, и
    // округлять её сильнее значило бы склеить соседние уровни стакана.
    price: formatPrice(t.price),
    usd: formatMoney(t.usd),
  }));
}

/**
 * Приписка честности под списком. Пусто — оговаривать нечего.
 *
 * Два разных умолчания, и оба обязаны быть названы. Обрезанная выдача выдавала
 * бы часть истории за всю; неразложенные роли — мейкерский объём за агрессию.
 */
export function walletTradesNote(data: MarketWalletTrades | undefined): string {
  if (!data) return "";
  const parts: string[] = [];
  if (data.truncated) {
    const n = data.trades.length;
    parts.push(
      `показаны первые ${n} ${plural(n, "сделка", "сделки", "сделок")} — их больше`,
    );
  }
  if (!data.roles_known) {
    parts.push(
      "роль разложена не везде: в части транзакций кошелёк стоял несколькими " +
        "ордерами, и какой из них сняли — по размеру и цене не определить",
    );
  }
  return parts.join(". ");
}

/**
 * Роль словами. Пустая строка — «не разложена», и это третье состояние, а не
 * умолчание: признака «тейкер» у строки источника нет вовсе, роль
 * восстанавливается по транзакции, и в неоднозначном случае прочерк честнее
 * правдоподобной ошибки (см. webapi.tradeRole).
 */
function roleLabel(t: MarketWalletTrade): string {
  if (t.role === "taker") return "тейкер";
  if (t.role === "maker") return "мейкер";
  return "";
}

/** Часы:минуты:секунды по МЕСТНОМУ времени — как везде в терминале. */
function formatClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/**
 * Смещение от начала окна: «+7 с» до минуты, «+2:19» дальше.
 *
 * Две формы, потому что читают их по-разному. В первую минуту важна секунда
 * («зашёл на седьмой»), дальше — минута с секундами, как на обратном отсчёте
 * окна: сравнивать «+139 с» с «осталось 2:41» оператор не должен в уме.
 */
function tauLabel(iso: string, start: Date | null): string {
  if (!start) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const sec = Math.round((t - start.getTime()) / 1000);
  const sign = sec < 0 ? "−" : "+";
  const abs = Math.abs(sec);
  if (abs < 60) return `${sign}${abs} с`;
  const m = Math.floor(abs / 60);
  const s = abs % 60;
  return `${sign}${m}:${String(s).padStart(2, "0")}`;
}

/** Цена до трёх знаков: шаг цены на этих рынках доходит до 0.001. */
function formatPrice(value: string): string {
  const m = parse(value);
  if (m === null) return "—";
  return roundToFixed(m, 3);
}

/** Деньги строки: «$50.00». Два знака — здесь это центы одной сделки. */
function formatMoney(value: string): string {
  const m = parse(value);
  if (m === null) return "—";
  return `$${roundToFixed(m, 2)}`;
}
